import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  DateTimeTimeZone,
  Event,
  MeetingTimeSuggestion,
  OnlineMeetingProviderType,
  ScheduleInformation,
} from '@microsoft/microsoft-graph-types';

import {
  GoogleCalendarAvailableTimesResponse,
  GoogleCalendarInvitationEmailEventParameters,
  GoogleCalendarInvitationEmailEventSnapshot,
  MeetingMinutesISO8601Duration,
} from '@libs/src/calendar/calendar.types';
import {
  ICalendarInvitationEmailEventParameters,
  ICalendarInvitationEmailEventSnapshot,
} from '@libs/src/calendar/icalendar/icalendar.types';
import { Match } from '@libs/src/models/match.interfaces';
import { ProgramPhase } from '@libs/src/models/program.model';
import { sanitizeGetParams } from '@main-client/src/app/opportunity/opportunity.helper';

@Injectable({
  providedIn: 'root',
})
export class CalendarBackend {
  readonly ICalendarInvitationEventApiUrl =
    '/api/v3/calendar/icalendar/invitation';
  constructor(private readonly http: HttpClient) {}

  getAuth(state: string) {
    return this.http.get<{ authorizationUrl: string }>(
      '/api/v3/calendar/auth',
      { params: sanitizeGetParams({ state }) },
    );
  }

  getGoogleAuth(state: string) {
    return this.http.get<{ authorizationUrl: string }>(
      '/api/v3/calendar/auth/google',
      { params: sanitizeGetParams({ state }) },
    );
  }

  getFindMeetingTimes(params: {
    dates: Date[];
    emailAddresses: string[];
    meetingTimeDuration: MeetingMinutesISO8601Duration;
    timeZone: DateTimeTimeZone['dateTime'];
  }) {
    return this.http.get<{
      result: { meetingTimeSuggestions: MeetingTimeSuggestion[] };
    }>('/api/v3/calendar/microsoft/meeting-times', {
      params: sanitizeGetParams(params),
    });
  }

  getSchedules(params: {
    availabilityViewInterval: number;
    emailAddresses: string[];
    endDateTime: string;
    startDateTime: string;
    timezone: DateTimeTimeZone['dateTime'];
  }) {
    return this.http.get<{ result: { value: ScheduleInformation[] } }>(
      '/api/v3/calendar/microsoft/schedules',
      {
        params: sanitizeGetParams(params),
      },
    );
  }

  postMicrosoftCalendarEvent(body: {
    event: {
      attendeeEmails: string[];
      content: string;
      endTime: Date;
      onlineMeetingProvider: OnlineMeetingProviderType;
      startTime: Date;
      subject: string;
    };
    matchIds: Array<Match['_id']>;
    phaseId?: ProgramPhase['_id'];
  }) {
    return this.http.post<{
      createdEvent: Event;
      match?: Match;
    }>('/api/v3/calendar/microsoft/events', body);
  }

  patchMicrosoftCalendarEvent(body: {
    event: {
      attendeeEmails: string[];
      content: string;
      endTime: Date;
      onlineMeetingProvider: OnlineMeetingProviderType;
      startTime: Date;
      subject: string;
    };
    matchIds: Array<Match['_id']>;
    phaseId?: ProgramPhase['_id'];
  }) {
    return this.http.patch<{
      updatedEvent: Event;
      match?: Match;
    }>('/api/v3/calendar/microsoft/events', body);
  }

  createICalendarInvitationEmailEvent(body: {
    event: ICalendarInvitationEmailEventParameters;
    matchIds: Array<Match['_id']>;
  }) {
    return this.http.post<ICalendarInvitationEmailEventSnapshot>(
      this.ICalendarInvitationEventApiUrl,
      body,
    );
  }

  updateICalendarInvitationEmailEvent(body: {
    event: ICalendarInvitationEmailEventParameters;
    matchIds: Array<Match['_id']>;
    phaseId?: ProgramPhase['_id'];
  }) {
    return this.http.patch<ICalendarInvitationEmailEventSnapshot>(
      this.ICalendarInvitationEventApiUrl,
      body,
    );
  }

  getICalendarInvitationEmailEvent(params: {
    matchId: Match['_id'];
    phaseId?: ProgramPhase['_id'];
  }) {
    return this.http.get<ICalendarInvitationEmailEventSnapshot>(
      this.ICalendarInvitationEventApiUrl,
      { params },
    );
  }

  getAdminConsentLink(params: { withOrgWidePermissions: boolean }) {
    return this.http.get<{ consentLink: string }>(
      '/api/v3/calendar/microsoft/admin-consent/link',
      {
        params: sanitizeGetParams(params),
      },
    );
  }

  getEvent(params: { eventId: string }) {
    return this.http.get<{ event: Event }>(
      `/api/v3/calendar/microsoft/events/${params.eventId}`,
    );
  }

  verifyGoogleWorkspaceConnection() {
    return this.http.get<{ isGoogleWorkspaceConnected: boolean }>(
      `/api/v3/calendar/google/workspace/verify`,
    );
  }

  getGoogleCalendarAvailableTimes(params: {
    emailAddresses: string[];
    endDateTime: string;
    startDateTime: string;
  }) {
    return this.http.get<GoogleCalendarAvailableTimesResponse>(
      '/api/v3/calendar/google/available-times',
      { params },
    );
  }

  updateGoogleInvitationEmailEvent(body: {
    event: GoogleCalendarInvitationEmailEventParameters;
    matchIds: Array<Match['_id']>;
    phaseId?: ProgramPhase['_id'];
  }) {
    return this.http.patch<GoogleCalendarInvitationEmailEventSnapshot>(
      '/api/v3/calendar/google/invitation',
      body,
    );
  }

  createGoogleInvitationEmailEvent(body: {
    event: GoogleCalendarInvitationEmailEventParameters;
    matchIds: Array<Match['_id']>;
    phaseId?: ProgramPhase['_id'];
  }) {
    return this.http.post<GoogleCalendarInvitationEmailEventSnapshot>(
      '/api/v3/calendar/google/invitation',
      body,
    );
  }

  getGoogleCalendarEvent(params: { matchId: string; phaseId?: string }) {
    return this.http.get<GoogleCalendarInvitationEmailEventSnapshot>(
      '/api/v3/calendar/google/invitation/',
      { params },
    );
  }
}
